import { FileUploadFileMimeType } from '@chakra-ui/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ChangeOrdersType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';

export type args = {
  postPendingChange: ApiType<ChangeOrdersType.Post>['args'];
  getPendingChanges: ApiType<ChangeOrdersType.Get>['args'];
  getPendingChange: ApiType<ChangeOrdersType.GetPendingChange>['args'];
  patchPendingChange: ApiType<ChangeOrdersType.Patch>['args'];

  // activity
  getPendingChangeActivity: ApiType<ChangeOrdersType.GetPendingChangeActivity>['args'];

  // attachments
  postPendingChangeAttachments: ApiType<ChangeOrdersType.PostPendingChangeAttachments>['args'];
  deletePendingChangeAttachment: ApiType<ChangeOrdersType.DeletePendingChangeAttachment>['args'];

  // delete change
  deletePendingChange: { params: { pending_change_id: number } };

  // export
  postPendingChangeExportPdf: ApiType<ChangeOrdersType.ExportPdf>['args'];

  // sheet settings
  getPendingChangeSheetSettings: { query: { project: number } };
  patchPendingChangeSheetSettings: { query: { project: number }; body: any };

  // presign upload
  postPendingChangeAttachmentS3PreSign: ApiType<ChangeOrdersType.PresignUpload>['args'];

  putPendingChangeAttachmentUploadToS3: {
    file: File;
    content_type: FileUploadFileMimeType;
    s3_url: string;
  };
};

export type responses = {
  postPendingChange: ApiType<ChangeOrdersType.Post>['response'];
  getPendingChanges: ApiType<ChangeOrdersType.Get>['response'];
  getPendingChange: ApiType<ChangeOrdersType.GetPendingChange>['response'];
  patchPendingChange: ApiType<ChangeOrdersType.Patch>['response'];

  getPendingChangeActivity: ApiType<ChangeOrdersType.GetPendingChangeActivity>['response'];

  postPendingChangeAttachments: ApiType<ChangeOrdersType.PostPendingChangeAttachments>['response'];
  deletePendingChangeAttachment: void;

  deletePendingChange: void;

  getPendingChangeSheetSettings: ApiType<ChangeOrdersType.GetPendingChangeSheetSettings>['response'];
  patchPendingChangeSheetSettings: ApiType<ChangeOrdersType.PatchPendingChangeSheetSettings>['response'];

  postPendingChangeExportPdf: Blob;

  postPendingChangeAttachmentS3PreSign: ApiType<ChangeOrdersType.PresignUpload>['response'];

  putPendingChangeAttachmentUploadToS3: any;
};

export const changeOrdersApi = createApi({
  reducerPath: 'changeOrdersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ChangeOrders'],

  endpoints: (builder) => ({
    postPendingChange: builder.mutation<
      responses['postPendingChange'],
      args['postPendingChange']
    >({
      query: ({ body }) => ({
        url: `/rfi/pending-changes/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result?.isError,
      }),
      invalidatesTags: () => [{ type: 'ChangeOrders', id: 'pending-changes' }],
    }),

    getPendingChanges: builder.query<
      responses['getPendingChanges'],
      args['getPendingChanges']
    >({
      query: ({ query }) => ({
        url: `/rfi/pending-changes/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'ChangeOrders', id: 'pending-changes' }],
    }),

    getPendingChange: builder.query<
      responses['getPendingChange'],
      args['getPendingChange']
    >({
      query: ({ params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_r, _e, arg) => [
        { type: 'ChangeOrders', id: 'pending-changes' },
        {
          type: 'ChangeOrders',
          id: `pending-change-${arg.params.pending_change_id}`,
        },
      ],
    }),

    patchPendingChange: builder.mutation<
      responses['patchPendingChange'],
      args['patchPendingChange']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ChangeOrders', id: 'pending-changes' },
        {
          type: 'ChangeOrders',
          id: `pending-change-${arg.params.pending_change_id}`,
        },
        {
          type: 'ChangeOrders',
          id: `pending-change-activity-${arg.params.pending_change_id}`,
        },
      ],
    }),

    getPendingChangeActivity: builder.query<
      responses['getPendingChangeActivity'],
      args['getPendingChangeActivity']
    >({
      query: ({ params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/activity/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      keepUnusedDataFor: 60,
      providesTags: (_r, _e, arg) => [
        {
          type: 'ChangeOrders',
          id: `pending-change-activity-${arg.params.pending_change_id}`,
        },
      ],
    }),

    // Export PDF (FULL PROJECT SHEET)
    postPendingChangeExportPdf: builder.mutation<
      responses['postPendingChangeExportPdf'],
      args['postPendingChangeExportPdf']
    >({
      query: ({ query }) => ({
        url: `/rfi/pending-changes/export/`,
        method: 'GET',
        params: query,
        responseHandler: (response: Response) => response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    // Delete
    deletePendingChange: builder.mutation<
      responses['deletePendingChange'],
      args['deletePendingChange']
    >({
      query: ({ params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/delete/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ChangeOrders', id: 'pending-changes' },
        {
          type: 'ChangeOrders',
          id: `pending-change-${arg.params.pending_change_id}`,
        },
      ],
    }),

    // Attachments list/create
    postPendingChangeAttachments: builder.mutation<
      responses['postPendingChangeAttachments'],
      args['postPendingChangeAttachments']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/attachments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result?.isError,
      }),
      invalidatesTags: (_r, _e, arg) => [
        {
          type: 'ChangeOrders',
          id: `pending-change-${arg.params.pending_change_id}`,
        },
      ],
    }),

    deletePendingChangeAttachment: builder.mutation<
      responses['deletePendingChangeAttachment'],
      args['deletePendingChangeAttachment']
    >({
      query: ({ params }) => ({
        url: `/rfi/pending-changes/${params.pending_change_id}/attachments/${params.attachment_id}/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_r, _e, arg) => [
        {
          type: 'ChangeOrders',
          id: `pending-change-${arg.params.pending_change_id}`,
        },
      ],
    }),

    postPendingChangeBulkUpdate: builder.mutation<
      any,
      { body: { updates: { id: number; patch: any }[] } }
    >({
      query: ({ body }) => ({
        url: `/rfi/pending-changes/bulk/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      invalidatesTags: [{ type: 'ChangeOrders', id: 'pending-changes' }],
    }),

    // Presign upload
    postPendingChangeAttachmentS3PreSign: builder.mutation<
      responses['postPendingChangeAttachmentS3PreSign'],
      args['postPendingChangeAttachmentS3PreSign']
    >({
      query: ({ body }) => ({
        url: `/rfi/pending-changes/attachments/presign/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
    }),

    // -----------------------------
    // Sheet Settings (per project)
    // GET /api/rfi/pending-changes/sheet-settings/?project=<id>
    // -----------------------------
    getPendingChangeSheetSettings: builder.query<
      responses['getPendingChangeSheetSettings'],
      args['getPendingChangeSheetSettings']
    >({
      query: ({ query }) => ({
        url: `/rfi/pending-changes/sheet-settings/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      keepUnusedDataFor: 3600,
      providesTags: () => [
        { type: 'ChangeOrders', id: 'pending-change-sheet-settings' },
      ],
    }),

    // -----------------------------
    // Sheet Settings update
    // PATCH /api/rfi/pending-changes/sheet-settings/?project=<id>
    // -----------------------------
    patchPendingChangeSheetSettings: builder.mutation<
      responses['patchPendingChangeSheetSettings'],
      args['patchPendingChangeSheetSettings']
    >({
      query: ({ query, body }) => ({
        url: `/rfi/pending-changes/sheet-settings/`,
        method: 'PATCH',
        params: query,
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      invalidatesTags: () => [
        { type: 'ChangeOrders', id: 'pending-change-sheet-settings' },
      ],
    }),

    // Upload file to S3
    putPendingChangeAttachmentUploadToS3: builder.mutation<
      responses['putPendingChangeAttachmentUploadToS3'],
      args['putPendingChangeAttachmentUploadToS3']
    >({
      query: ({ file, content_type, s3_url }) => ({
        url: s3_url,
        method: 'PUT',
        headers: { 'Content-Type': content_type },
        body: file,
        validateStatus: (response) => response.status === 200,
      }),
    }),
  }),
});

export const {
  usePostPendingChangeMutation,
  useGetPendingChangesQuery,
  useGetPendingChangeQuery,
  usePatchPendingChangeMutation,
  useGetPendingChangeActivityQuery,
  useDeletePendingChangeMutation,
  usePostPendingChangeExportPdfMutation,
  usePostPendingChangeAttachmentsMutation,
  useDeletePendingChangeAttachmentMutation,
  usePostPendingChangeAttachmentS3PreSignMutation,
  usePutPendingChangeAttachmentUploadToS3Mutation,
  usePostPendingChangeBulkUpdateMutation,
  useGetPendingChangeSheetSettingsQuery,
  usePatchPendingChangeSheetSettingsMutation,
} = changeOrdersApi;

export namespace ChangeOrdersApi {
  export const Api = changeOrdersApi;
  export type Args = args;
  export type Responses = responses;
}
